import React, { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus } from 'lucide-react';
import { VALID_ACCOUNT_TYPES } from '../components/ImportChartOfAccountsModal/constants';
import { useChartOfAccounts } from '../../../../hooks/useChartOfAccounts';
import {
  createEmptyRow,
  validateRows,
  parsePastedGrid,
  applyPastedGrid,
  type DraftRow,
  type DraftField,
} from '../../../../hooks/useCreateMultipleAccounts';
import HintStrip from './HintStrip';
import AccountRowEditor from './AccountRowEditor';
import SaveBar from './SaveBar';
import { INITIAL_ROWS, GRID_TEMPLATE } from './constants';

export function CreateMultipleHeader() {
  const navigate = useNavigate();
  return (
    <div className="px-4 py-3.5 md:px-8">
      <button
        type="button"
        onClick={() => navigate('/accounting/chart-of-accounts')}
        className="inline-flex items-center gap-0.5 text-[12.5px] font-semibold bg-transparent border-none p-0 cursor-pointer hover:underline mb-1.5"
        style={{ color: '#0F6E56' }}
      >
        <span className="text-[15px] leading-none mr-0.5">‹</span> Back to chart of accounts
      </button>
      <div className="text-[12.5px] text-ink3 mb-1 font-medium">
        <span className="cursor-pointer hover:text-ink" onClick={() => navigate('/accounting')}>Accounting</span>
        <span className="text-ink3 mx-1.5">/</span>
        <span className="cursor-pointer hover:text-ink" onClick={() => navigate('/accounting/chart-of-accounts')}>Chart of accounts</span>
        <span className="text-ink3 mx-1.5">/</span>
        <span>Create multiple accounts</span>
      </div>
      <h1 className="text-xl md:text-[22px] font-extrabold m-0 -tracking-[0.3px] text-ink leading-none">Create multiple accounts</h1>
    </div>
  );
}

export default function CreateMultiplePage() {
  const navigate = useNavigate();
  const { data: existingAccounts, addAccount } = useChartOfAccounts();
  const existingNames = useMemo(() => existingAccounts.map((a: { name: string }) => a.name), [existingAccounts]);
  const [rows, setRows] = useState<DraftRow[]>(() => Array.from({ length: INITIAL_ROWS }, () => createEmptyRow()));
  const [showErrors, setShowErrors] = useState(false);

  const validated = useMemo(() => validateRows(rows, existingNames, VALID_ACCOUNT_TYPES), [rows, existingNames]);
  const readyCount = validated.filter((v) => v.status === 'ready').length;
  const errorCount = validated.filter((v) => v.status === 'error').length;
  const emptyCount = validated.filter((v) => v.status === 'empty').length;
  const totalFilled = readyCount + errorCount;

  const updateCell = useCallback((idx: number, field: DraftField, value: string) => {
    setRows((prev) => prev.map((r, i) => (i === idx ? { ...r, [field]: value } : r)));
  }, []);

  const addRow = useCallback(() => {
    setRows((prev) => [...prev, createEmptyRow()]);
  }, []);

  const deleteRow = useCallback((idx: number) => {
    setRows((prev) => (prev.length > 1 ? prev.filter((_, i) => i !== idx) : [createEmptyRow()]));
  }, []);

  const handlePaste = useCallback((e: React.ClipboardEvent<HTMLInputElement>, startIdx: number, col: DraftField) => {
    const text = e.clipboardData.getData('text/plain');
    if (!text.includes('\t') && !text.includes('\n')) return;
    e.preventDefault();
    const parsed = parsePastedGrid(text);
    setRows((prev) => applyPastedGrid(prev, startIdx, col, parsed));
  }, []);

  const createAll = useCallback(() => {
    if (totalFilled === 0) return;
    if (errorCount > 0) {
      setShowErrors(true);
      return;
    }
    validated.filter((v) => v.status === 'ready').forEach((row) => {
      addAccount({ number: row.number, name: row.name, type: row.accountType as never });
    });
    navigate('/accounting/chart-of-accounts');
  }, [validated, errorCount, totalFilled, addAccount, navigate]);

  const skipErrorsAndCreate = useCallback(() => {
    const ready = validated.filter((v) => v.status === 'ready');
    ready.forEach((row) => {
      addAccount({ number: row.number, name: row.name, type: row.accountType as never });
    });
    if (ready.length > 0) {
      navigate('/accounting/chart-of-accounts');
    }
  }, [validated, addAccount, navigate]);

  const cancel = useCallback(() => navigate('/accounting/chart-of-accounts'), [navigate]);
  const primaryDisabled = totalFilled === 0 || (showErrors && errorCount > 0);

  return (
    <div style={{ maxWidth: '100%', margin: 0, padding: '0 32px 90px' }}>
      <div className="pt-4">
        <HintStrip />
        <div className="grid gap-2.5 px-4 py-2.5 bg-panel border border-line rounded-t-lg text-[11px] font-bold text-ink2 uppercase tracking-wider"
          style={{ gridTemplateColumns: GRID_TEMPLATE }}>
          <div></div>
          <div>Number</div>
          <div>Account name</div>
          <div>Account type</div>
          <div>Detail type</div>
          <div></div>
        </div>
        <div className="border border-line border-t-0 rounded-b-lg">
          {validated.map((row, idx) => (
            <AccountRowEditor
              key={row.id}
              row={row}
              index={idx}
              showError={showErrors}
              onUpdateCell={updateCell}
              onPaste={handlePaste}
              onDelete={deleteRow}
            />
          ))}
        </div>
        <button
          type="button"
          onClick={addRow}
          className="mt-3 inline-flex items-center gap-1.5 px-3.5 py-2 border border-dashed border-line rounded-lg bg-transparent text-[13px] font-semibold cursor-pointer hover:bg-[#E6F2EE] hover:border-teal-dark"
          style={{ color: '#0F6E56' }}
        >
          <Plus className="w-3.5 h-3.5" />
          Add row
        </button>
      </div>
      <SaveBar
        readyCount={readyCount}
        errorCount={errorCount}
        emptyCount={emptyCount}
        totalFilled={totalFilled}
        showErrors={showErrors}
        primaryDisabled={primaryDisabled}
        onCancel={cancel}
        onCreate={createAll}
        onSkipErrors={skipErrorsAndCreate}
      />
    </div>
  );
}